/**
 * EntityCommandPalette — modal popover with every per-entity action and a
 * search-first input (doc 147 §5.3 world-class redesign · PR B).
 *
 * Open with `e` while an entity sheet is focused, or via the More button
 * below the answer tiles. Each row carries the precomputed outcome
 * ("1 entity", "✓ clean", "PII · 7 downstream") so the user can decide if
 * the destination is worth opening without committing to a navigation.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  buildEntityCommands,
  filterCommands,
  type EntityCommand,
} from '../state/entityCommands.js';
import type { InsightIndex } from '../state/entityInsights.js';
import { navigate } from '../state/hashRouter.js';
import { Icon } from '../components/Icon.js';
import { useFocusTrap } from '../state/useFocusTrap.js';

export interface EntityCommandPaletteProps {
  index: InsightIndex;
  entityId: string;
  onClose: () => void;
}

const GROUP_LABELS: Record<EntityCommand['group'], string> = {
  entity: 'For this entity',
  quality: 'Quality & verification',
  generate: 'Generate & explore',
  tools: 'Tools',
};

const GROUP_ORDER: EntityCommand['group'][] = ['entity', 'quality', 'generate', 'tools'];

export function EntityCommandPalette({
  index,
  entityId,
  onClose,
}: EntityCommandPaletteProps): React.JSX.Element {
  const all = useMemo(() => buildEntityCommands(index, entityId), [index, entityId]);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();

  useFocusTrap(panelRef, true);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => filterCommands(all, query), [all, query]);
  // Keep the highlight valid when the list shrinks.
  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(0);
  }, [filtered.length, highlight]);

  const grouped = useMemo(() => {
    if (query.trim().length > 0) {
      // While searching, show a single flat list ordered by score.
      return [{ group: 'entity' as const, label: '', items: filtered }];
    }
    return GROUP_ORDER.map((g) => ({
      group: g,
      label: GROUP_LABELS[g],
      items: filtered.filter((c) => c.group === g),
    })).filter((s) => s.items.length > 0);
  }, [filtered, query]);

  const flatItems = useMemo(() => grouped.flatMap((s) => s.items), [grouped]);

  const activate = (cmd: EntityCommand): void => {
    navigate(cmd.route);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(flatItems.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = flatItems[highlight] ?? flatItems[0];
      if (pick) activate(pick);
    }
  };

  return (
    <div
      className="ecp-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={panelRef}
        className="ecp-panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Actions for ${entityId}`}
        onKeyDown={onKeyDown}
      >
        <header className="ecp-head">
          <Icon name="search" size="sm" />
          <input
            ref={inputRef}
            role="combobox"
            aria-label={`Actions for ${entityId}`}
            aria-expanded="true"
            aria-controls={listboxId}
            aria-autocomplete="list"
            placeholder={`Actions for ${entityId}…`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlight(0);
            }}
          />
          <kbd className="ecp-esc" aria-hidden="true">
            esc
          </kbd>
        </header>
        <div className="ecp-list" id={listboxId} role="listbox">
          {grouped.length === 0 && (
            <div className="ecp-empty" role="status">
              No matches.
            </div>
          )}
          {grouped.map((section) => (
            <div key={section.group} className="ecp-section">
              {section.label && <div className="ecp-section-label">{section.label}</div>}
              {section.items.map((cmd) => {
                const idx = flatItems.indexOf(cmd);
                const active = idx === highlight;
                return (
                  <div
                    key={cmd.id}
                    role="option"
                    aria-selected={active}
                    className={`ecp-row${active ? ' is-active' : ''}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      activate(cmd);
                    }}
                    onMouseEnter={() => setHighlight(idx)}
                  >
                    <span className="ecp-label">{cmd.label}</span>
                    <span className="ecp-outcome">{cmd.outcome}</span>
                    {cmd.shortcut && (
                      <kbd className="ecp-shortcut" aria-hidden="true">
                        {cmd.shortcut}
                      </kbd>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
